import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Fee,
  FeeStructure,
  Payment,
  CreateFeeStructureRequest,
  UpdateFeeStructureRequest,
  CreatePaymentRequest,
  EnhancedFeeFilter as FeeFilter,
  EnhancedPaymentFilter as PaymentFilter,
  FeeStatistics,
} from "../types/fee";
import api from "./api";
import axios from "axios";

// Define UnpaidStudent interface
export interface UnpaidStudent {
  _id: string;
  studentId: string;
  firstName: string;
  lastName: string;
  currentInstallment: {
    installmentNumber: number;
    amount: number;
    dueDate: string;
    status: string;
  };
  totalOutstanding: number;
  daysOverdue: number;
}

// Fee Structure CRUD Operations
export const getFeeStructures = async (
  filters: FeeFilter = {}
): Promise<PaginatedResponse<FeeStructure>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.classId) params.append("classId", filters.classId);
  if (filters.academicYear) params.append("academicYear", filters.academicYear);
  if (filters.academicTermId)
    params.append("academicTermId", filters.academicTermId);
  if (filters.isActive !== undefined)
    params.append("isActive", filters.isActive.toString());

  const response: any = await api.get(`/fees/structures?${params.toString()}`);
  return response as PaginatedResponse<FeeStructure>;
};

export const getFeeStructureById = async (
  id: string
): Promise<ApiResponse<FeeStructure>> => {
  const response = await api.get(`/fees/structures/${id}`);
  return response.data;
};

export const createFeeStructure = async (
  data: CreateFeeStructureRequest
): Promise<ApiResponse<FeeStructure>> => {
  const response = await api.post("/fees/structures", data);
  return response.data;
};

export const updateFeeStructure = async (
  id: string,
  data: UpdateFeeStructureRequest
): Promise<ApiResponse<FeeStructure>> => {
  const response = await api.put(`/fees/structures/${id}`, data);
  return response.data;
};

export const deleteFeeStructure = async (
  id: string
): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/fees/structures/${id}`);
  return response.data;
};

// Assign fees to students
export const assignFeesToStudents = async (data: {
  feeStructureId: string;
  studentIds: string[];
  applyDiscount?: boolean;
  discountAmount?: number;
  discountReason?: string;
}): Promise<ApiResponse<Fee[]>> => {
  const response = await api.post("/fees/assign", data);
  return response.data;
};

// Fee Management Operations
export const getFees = async (
  filters: FeeFilter = {}
): Promise<PaginatedResponse<Fee>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.studentId) params.append("studentId", filters.studentId);
  if (filters.classId) params.append("classId", filters.classId);
  if (filters.status) params.append("status", filters.status);
  if (filters.dueDate) params.append("dueDate", filters.dueDate);

  const response = await api.get(`/fees/outstanding?${params.toString()}`);
  return response.data;
};

export const getFeeById = async (id: string): Promise<ApiResponse<Fee>> => {
  const response = await api.get(`/fees/${id}`);
  return response.data;
};

export const updateFee = async (
  id: string,
  data: Partial<Fee>
): Promise<ApiResponse<Fee>> => {
  const response = await api.put(`/fees/${id}`, data);
  return response.data;
};

export const createFee = async (
  data: Partial<Fee>
): Promise<ApiResponse<Fee>> => {
  const response = await api.post("/fees", data);
  return response.data;
};

// Payment Operations
export const getPayments = async (
  filters: PaymentFilter = {}
): Promise<PaginatedResponse<Payment>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.studentId) params.append("studentId", filters.studentId);
  if (filters.feeId) params.append("feeId", filters.feeId);
  if (filters.paymentMethod)
    params.append("paymentMethod", filters.paymentMethod);
  if (filters.status) params.append("status", filters.status);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const response = await api.get(`/fees/payments?${params.toString()}`);
  return response.data;
};

export const createPayment = async (
  data: CreatePaymentRequest
): Promise<ApiResponse<Payment>> => {
  const response = await api.post("/fees/payments/manual", data);
  return response.data;
};

export const getPaymentById = async (
  id: string
): Promise<ApiResponse<Payment>> => {
  const response = await api.get(`/payments/${id}`);
  return response.data;
};

export const getPaymentStatus = async (
  paymentId: string
): Promise<ApiResponse<Payment>> => {
  const response = await api.get(`/fees/payments/${paymentId}/status`);
  return response.data;
};

// Mpesa Payment Operations
export const initiateMpesaPayment = async (data: {
  feeId: string;
  amount: number;
  phoneNumber: string;
}): Promise<
  ApiResponse<{
    paymentId: string;
    checkoutRequestId: string;
    merchantRequestId: string;
    amount: number;
    phoneNumber: string;
  }>
> => {
  const response = await api.post("/fees/payments/mpesa/initiate", data);
  return response.data;
};

// Student course fee payment (no feeId required)
export const initiateStudentMpesaPayment = async (data: {
  amount: number;
  phoneNumber: string;
  studentId?: string; // Optional - for secretary-initiated payments
}): Promise<
  ApiResponse<{
    paymentId: string;
    checkoutRequestId: string;
    merchantRequestId: string;
  }>
> => {
  const response = await api.post(
    "/fees/payments/student/mpesa/initiate",
    data
  );
  return response.data;
};

export const checkPaymentStatus = async (
  paymentId: string
): Promise<ApiResponse<Payment>> => {
  const response = await api.get(`/fees/payments/${paymentId}/status`);
  return response.data;
};

// Equity Payment Operations
export const initiateEquityPayment = async (data: {
  feeId: string;
  amount: number;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
}): Promise<
  ApiResponse<{
    paymentId: string;
    orderReference: string;
    checkoutUrl: string;
    checkoutData: any;
  }>
> => {
  const response = await api.post("/fees/payments/equity/initiate", data);
  return response.data;
};

export const initiateStudentEquityPayment = async (data: {
  amount: number;
  phoneNumber: string;
  studentId?: string;
}): Promise<
  ApiResponse<{
    paymentId: string;
    orderReference: string;
    paymentReference: string;
    invoiceNumber: string;
    amount: number;
    charge: number;
    amountDebited: number;
  }>
> => {
  const response = await api.post(
    "/fees/payments/student/equity/initiate",
    data
  );
  return response as unknown as ApiResponse<{
    paymentId: string;
    orderReference: string;
    paymentReference: string;
    invoiceNumber: string;
    amount: number;
    charge: number;
    amountDebited: number;
  }>;
};

// Fee Statistics
export const getFeeStatistics = async (
  params: string
): Promise<ApiResponse<FeeStatistics>> => {
  const response: any = await api.get(`/fees/${params}`);
  return response as ApiResponse<FeeStatistics>;
};

// Student Fee Operations
export const getStudentFees = async (
  studentId: string
): Promise<ApiResponse<Fee[]>> => {
  const response = await api.get(`/students/${studentId}/fees`);
  return response.data;
};

export const markFeeAsPaid = async (
  feeId: string,
  paymentData: CreatePaymentRequest
): Promise<ApiResponse<Payment>> => {
  const response = await api.post(`/fees/${feeId}/pay`, paymentData);
  return response.data;
};

// Bulk Operations
export const bulkGenerateFees = async (
  classId: string,
  academicYear: string
): Promise<ApiResponse<void>> => {
  const response = await api.post("/fees/bulk-generate", {
    classId,
    academicYear,
  });
  return response.data;
};

export const sendFeeReminders = async (
  studentIds: string[]
): Promise<ApiResponse<void>> => {
  const response = await api.post("/fees/send-reminders", { studentIds });
  return response.data;
};

// Get student fee summaries for branch admin
export const getBranchStudentFeeSummaries = async (
  filters: {
    page?: number;
    limit?: number;
    search?: string;
    classId?: string;
    academicYear?: string;
    sortBy?: string;
    sortOrder?: string;
    balanceFilter?: string;
    viewType?: string;
    periodYear?: number;
    periodMonth?: number;
  } = {}
): Promise<PaginatedResponse<any>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.classId) params.append("classId", filters.classId);
  if (filters.academicYear) params.append("academicYear", filters.academicYear);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  if (filters.balanceFilter)
    params.append("balanceFilter", filters.balanceFilter);
  if (filters.viewType) params.append("viewType", filters.viewType);
  if (filters.periodYear) params.append("periodYear", filters.periodYear.toString());
  if (filters.periodMonth) params.append("periodMonth", filters.periodMonth.toString());

  const response: any = await api.get(
    `/fees/branch/students?${params.toString()}`
  );
  return response as PaginatedResponse<any>;
};

// Receipt Generation
export const generateReceipt = async (
  paymentId: string
): Promise<ApiResponse<{ receiptUrl: string }>> => {
  const response = await api.get(`/fees/receipts/${paymentId}`);
  return response.data;
};

export const downloadReceipt = async (paymentId: string): Promise<Blob> => {
  // Use axios directly to bypass the response interceptor for blob responses
  const response = await axios.get(
    `${api.defaults.baseURL}/fees/receipts/${paymentId}/download`,
    {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }
  );
  return response.data;
};

// Generate receipt for student payment history entry (for embedded payments)
export const generateStudentPaymentReceipt = async (
  studentId: string,
  paymentReference: string
): Promise<ApiResponse<{ receiptUrl: string }>> => {
  // Use axios directly to avoid response interceptor issues
  const response = await axios.get(
    `${api.defaults.baseURL}/students/${studentId}/payment-receipt/${paymentReference}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const downloadStudentPaymentReceipt = async (
  studentId: string,
  paymentReference: string
): Promise<Blob> => {
  // Use axios directly to bypass the response interceptor for blob responses
  const response = await axios.get(
    `${api.defaults.baseURL}/students/${studentId}/payment-receipt/${paymentReference}/download`,
    {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }
  );
  return response.data;
};

// Fee Assignment Operations with Dynamic Scheduling
export const assignFeeToStudent = async (
  data: import("../types/fee").AssignFeeToStudentRequest
): Promise<ApiResponse<Fee>> => {
  const response = await api.post("/fees/assign", data);
  return response.data;
};

export const bulkAssignFees = async (
  data: import("../types/fee").BulkAssignFeeRequest
): Promise<ApiResponse<Fee[]>> => {
  const response = await api.post("/fees/bulk-assign", data);
  return response.data;
};

// Get student's fee schedule with payment timeline
export const getStudentFeeSchedule = async (
  studentId: string,
  feeId?: string
): Promise<
  ApiResponse<{
    fees: Fee[];
    upcomingPayments: import("../types/fee").InstallmentDetail[];
    overduePaments: import("../types/fee").InstallmentDetail[];
  }>
> => {
  const params = feeId ? `?feeId=${feeId}` : "";
  const response = await api.get(
    `/students/${studentId}/fee-schedule${params}`
  );
  return response.data;
};

// Update student's payment schedule (in case of changes like deferment)
export const updateStudentPaymentSchedule = async (
  feeId: string,
  updates: {
    newStartDate?: string;
    extendDueDates?: number; // days to extend
    adjustInstallmentAmount?: number;
    reason?: string;
  }
): Promise<ApiResponse<Fee>> => {
  const response = await api.put(`/fees/${feeId}/schedule`, updates);
  return response.data;
};

// Calculate payment schedule preview before assignment
export const calculatePaymentSchedule = async (
  feeStructureId: string,
  joinDate: string,
  customInstallmentPlan?: {
    enabled: boolean;
    numberOfInstallments?: number;
    frequency?: "monthly" | "quarterly" | "weekly";
  }
): Promise<
  ApiResponse<{
    totalAmount: number;
    installmentAmount: number;
    paymentSchedule: import("../types/fee").InstallmentDetail[];
  }>
> => {
  const response = await api.post("/fees/calculate-schedule", {
    feeStructureId,
    joinDate,
    customInstallmentPlan,
  });
  return response.data;
};

// Secretary Functions
export const getUnpaidStudents = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<UnpaidStudent>> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const response: any = await api.get(
    `/fees/secretary/unpaid-students?${params.toString()}`
  );
  return response as PaginatedResponse<UnpaidStudent>;
};

export const sendPaymentReminders = async (studentIds: string[]) => {
  const response = await api.post("/fees/secretary/send-reminders", {
    studentIds,
  });
  return response;
};
